import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

const inputCls = "mt-2 rounded-none bg-white border-black/15 focus-visible:ring-2 focus-visible:ring-[#ED5A24] focus-visible:ring-offset-0 h-11";
const labelCls = "text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]";
const btnCls = "w-full rounded-none bg-[#ED5A24] hover:bg-[#C44619] text-white text-xs uppercase tracking-[0.2em] font-bold h-12";

const Field = ({ id, label, ...props }) => (
  <div>
    <Label htmlFor={id} className={labelCls}>{label}</Label>
    <Input id={id} required className={inputCls} {...props} />
  </div>
);

export default function ForgotPassword() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const run = async (fn) => {
    setLoading(true);
    try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const sendCode = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email has an account, a code is on its way");
      setStep("code");
    });
  };

  const reset = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/auth/reset-password", { email, code, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="forgot-page">
      <Header />
      <div className="max-w-md mx-auto px-6 py-16 md:py-24">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-3">Reset password</div>
        <h1 className="text-4xl md:text-5xl tracking-tighter font-black mb-6">Forgot it? No stress.</h1>
        {step === "email" ? (
          <form onSubmit={sendCode} className="border border-black/10 bg-[#F8F9FA] p-8 space-y-6" data-testid="forgot-email-form">
            <p className="text-sm text-[#52525B]">Enter your account email and we'll send you a 6-digit code.</p>
            <Field id="email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@studio.com" data-testid="forgot-email-input" />
            <Button type="submit" disabled={loading} className={btnCls} data-testid="forgot-send-code-btn">
              {loading ? "Sending…" : "Send code"} <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        ) : (
          <form onSubmit={reset} className="border border-black/10 bg-[#F8F9FA] p-8 space-y-6" data-testid="forgot-reset-form">
            <p className="text-sm text-[#52525B]">We sent a code to <strong>{email}</strong>. It expires in 15 minutes.</p>
            <Field id="code" label="6-digit code" inputMode="numeric" pattern="\d{6}" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" data-testid="forgot-code-input" />
            <Field id="new-password" label="New password" type="password" minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="forgot-new-password-input" />
            <Button type="submit" disabled={loading} className={btnCls} data-testid="forgot-reset-btn">
              {loading ? "Saving…" : "Set new password"} <ArrowRight className="w-4 h-4" />
            </Button>
            <button type="button" onClick={() => setStep("email")} className="text-sm text-[#ED5A24] underline underline-offset-4 w-full" data-testid="forgot-resend-btn">
              Didn't get it? Send a new code
            </button>
          </form>
        )}
        <div className="text-sm text-[#52525B] text-center mt-6">
          <Link to="/login" className="text-[#ED5A24] underline underline-offset-4" data-testid="forgot-back-to-login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
